// A poll rendered in the thread: the question + options with live result bars. Tap an
// option to vote (single-choice replaces; multi toggles). Optimistic via the hook.
import React from 'react';
import type { MessagePoll } from '../types';

export const PollMessage: React.FC<{ poll: MessagePoll; onVote: (optionIds: string[]) => void }> = ({ poll, onVote }) => {
  const counts = poll.counts ?? {};
  const mine = new Set(poll.my_votes ?? []);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const toggle = (id: string) => {
    if (poll.multi) {
      const next = new Set(mine);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onVote([...next]);
    } else {
      onVote(mine.has(id) ? [] : [id]);
    }
  };

  return (
    <div className="w-full max-w-[80%] rounded-2xl border border-border bg-card p-3">
      <p className="mb-2 text-sm font-semibold text-charcoal">📊 {poll.question}</p>
      <div className="space-y-1.5">
        {poll.options.map((o) => {
          const n = counts[o.id] ?? 0;
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          const voted = mine.has(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              className={`relative block w-full overflow-hidden rounded-lg border text-left ${
                voted ? 'border-primary-300' : 'border-border'
              }`}
            >
              <div className="absolute inset-y-0 left-0 bg-primary-100 transition-[width] dark:bg-primary-500/20" style={{ width: `${pct}%` }} />
              <div className="relative flex items-center justify-between px-2.5 py-1.5 text-xs">
                <span className={voted ? 'font-semibold text-primary-700' : 'text-charcoal'}>
                  {voted ? '✓ ' : ''}
                  {o.text}
                </span>
                <span className="flex-none pl-2 text-muted-foreground">
                  {n} · {pct}%
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        {total} vote{total === 1 ? '' : 's'}
        {poll.multi ? ' · multiple answers' : ''}
      </p>
    </div>
  );
};
